import { Injectable, inject, resource, computed, DestroyRef } from "@angular/core";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { AuthService, type CurrentUser } from "@shared/auth";
import { DashboardsService } from "@shared/dashboards-service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

type AccessibleContacts = Set<number> | undefined;

interface AccessibleContactsResource {
    currentUser: CurrentUser | null;
    isJobsBilling: boolean;
}

@Injectable({ providedIn: "root" })
export class IFirmContactsService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly dashboardsService = inject(DashboardsService);

    private isReloading = true;
    private refreshTimer = setInterval(() => this.reload(), 3600000);

    constructor() {
        this.destroyRef.onDestroy(() => clearInterval(this.refreshTimer));

        this.authService.sessionData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.reload());
    }

    private reload(): void {
        if (this.authService.inReloginPrompted()) return;
        this.isReloading = true;
        this.accessibleContactsResource.reload();
    }

    private cachedAccessibleContacts: AccessibleContacts;

    private accessibleContactsResource = resource<AccessibleContacts, AccessibleContactsResource>({
        params: () => ({
            currentUser: this.authService.currentUser(),
            isJobsBilling: this.dashboardsService.currentDashboardIsJobsBilling(),
        }),
        loader: async ({ params: { currentUser, isJobsBilling } }) => {
            if (!isJobsBilling || !currentUser || this.authService.inReloginPrompted() || (this.cachedAccessibleContacts && !this.isReloading)) {
                return this.cachedAccessibleContacts;
            }

            this.isReloading = false;
            const accessibleContacts = await this.getAccessibleContacts();
            if (accessibleContacts) this.cachedAccessibleContacts = accessibleContacts;
            return accessibleContacts;
        },
    });
    public accessibleContacts = computed(() => this.accessibleContactsResource.value() ?? this.cachedAccessibleContacts);

    public isContactAccessible(contactId: number): boolean {
        return this.accessibleContacts()?.has(contactId) ?? false;
    }

    private async getAccessibleContacts(): Promise<AccessibleContacts> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getAccessibleContacts", {}, "gotAccessibleContacts");
            if (result.status === "success") return new Set(result.response.Data as number[]);
            else {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "defaultError",
                });
            }
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }
}
